import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fmtDate } from "@/lib/dates";
import { Plus, Trash2, Pencil, X, Check } from "lucide-react";

const STATE_CLS = {
  overdue: "bg-rag-red-bg text-rag-red", due_soon: "bg-rag-amber-bg text-rag-amber", ok: "bg-rag-green-bg text-rag-green",
  open: "bg-brand-light text-brand", appointment_booked: "bg-rag-amber-bg text-rag-amber", completed: "bg-rag-green-bg text-rag-green", cancelled: "bg-cream text-muted-foreground",
};
const STATE_LABEL = { overdue: "Overdue", due_soon: "Due soon", ok: "Up to date", open: "Open", appointment_booked: "Appointment booked", completed: "Completed", cancelled: "Cancelled" };

export const Pill = ({ state, testId }) => (
  <span data-testid={testId} className={`inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${STATE_CLS[state] || "bg-cream"}`}>{STATE_LABEL[state] || state}</span>
);

function Field({ f, value, onChange, testId }) {
  const cls = "h-8 text-xs bg-white";
  if (f.readOnly) return <span data-testid={testId} className="text-xs text-muted-foreground">{f.readOnlyText || "Calculated on save"}</span>;
  if (f.type === "select") {
    return (
      <select data-testid={testId} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={`${cls} rounded-md border border-input px-2 w-full`}>
        {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }
  return <Input data-testid={testId} type={f.type || "text"} value={value ?? ""} placeholder={f.placeholder} onChange={(e) => onChange(f.type === "number" ? Number(e.target.value) : e.target.value)} className={cls} />;
}

function RowForm({ fields, initial, onSave, onCancel, testPrefix }) {
  const [v, setV] = useState(initial);
  const [busy, setBusy] = useState(false);
  const submit = async () => { setBusy(true); try { await onSave(v); } finally { setBusy(false); } };
  return (
    <tr data-testid={`${testPrefix}-form`} className="bg-cream/60">
      {fields.map((f) => <td key={f.key} className="px-3 py-2 align-top"><Field f={f} value={v[f.key]} onChange={(x) => setV({ ...v, [f.key]: x })} testId={`${testPrefix}-${f.key}`} /></td>)}
      <td className="px-3 py-2 align-top whitespace-nowrap">
        <button data-testid={`${testPrefix}-save`} onClick={submit} disabled={busy} className="text-rag-green hover:bg-rag-green-bg rounded p-1 mr-1"><Check className="h-4 w-4" /></button>
        <button data-testid={`${testPrefix}-cancel`} onClick={onCancel} className="text-muted-foreground hover:bg-cream rounded p-1"><X className="h-4 w-4" /></button>
      </td>
    </tr>
  );
}

export function RecordsLog({ id, testId, title, subtitle, endpoint, fields, columns, emptyText, blank, addLabel, sortKey, sortDesc = false, onChanged }) {
  const [rows, setRows] = useState([]);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => { api.get(endpoint).then((r) => setRows(r.data)).catch(() => {}); }, [endpoint]);

  const sorted = (list) => sortKey ? [...list].sort((a, b) => (sortDesc ? -1 : 1) * String(a[sortKey] || "").localeCompare(String(b[sortKey] || ""))) : list;
  const changed = () => onChanged && onChanged();
  const create = async (v) => {
    try { const { data } = await api.post(endpoint, v); setRows((r) => sorted([data, ...r])); setAdding(false); toast.success("Record added"); changed(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const update = async (rowId, v) => {
    try { const { data } = await api.put(`${endpoint}/${rowId}`, v); setRows((r) => sorted(r.map((x) => (x.id === rowId ? data : x)))); setEditing(null); toast.success("Record updated"); changed(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const remove = async (row) => {
    if (!window.confirm(`Remove the record for ${row.resident_ref}? This cannot be undone.`)) return;
    try { await api.delete(`${endpoint}/${row.id}`); setRows((r) => r.filter((x) => x.id !== row.id)); toast.success("Record removed"); changed(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <section id={id} data-testid={testId} className="rounded-2xl bg-cream-card border border-cream-deep overflow-hidden mb-4 scroll-mt-24">
      <div className="px-5 py-3 border-b border-cream-deep flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="font-heading font-bold text-brand text-sm">{title}</div>
          <div className="text-xs text-muted-foreground">{subtitle}</div>
        </div>
        <Button data-testid={`${testId}-add`} size="sm" onClick={() => setAdding(true)} disabled={adding} className="rounded-full bg-brand hover:bg-brand-hover text-white h-8"><Plus className="h-3.5 w-3.5 mr-1" />{addLabel}</Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left label-caps text-muted-foreground bg-cream">{fields.map((f) => <th key={f.key} className="px-3 py-2 font-semibold">{f.label}</th>)}<th className="px-3 py-2" /></tr></thead>
          <tbody className="divide-y divide-cream">
            {adding && <RowForm fields={fields} initial={blank()} onSave={create} onCancel={() => setAdding(false)} testPrefix={`${testId}-new`} />}
            {rows.length === 0 && !adding && <tr><td colSpan={fields.length + 1} className="px-5 py-5 text-xs text-muted-foreground" data-testid={`${testId}-empty`}>{emptyText}</td></tr>}
            {rows.map((r) => editing === r.id ? (
              <RowForm key={r.id} fields={fields} initial={r} onSave={(v) => update(r.id, v)} onCancel={() => setEditing(null)} testPrefix={`${testId}-edit`} />
            ) : (
              <tr key={r.id} data-testid={`${testId}-row-${r.id}`}>
                {fields.map((f) => <td key={f.key} className="px-3 py-2 align-top">{(columns[f.key] || ((row) => row[f.key] || "—"))(r)}</td>)}
                <td className="px-3 py-2 align-top whitespace-nowrap">
                  <button data-testid={`${testId}-edit-${r.id}`} onClick={() => setEditing(r.id)} className="text-brand hover:bg-brand-light rounded p-1 mr-1"><Pencil className="h-3.5 w-3.5" /></button>
                  <button data-testid={`${testId}-delete-${r.id}`} onClick={() => remove(r)} className="text-rag-red hover:bg-rag-red-bg rounded p-1"><Trash2 className="h-3.5 w-3.5" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const today = () => new Date().toISOString().slice(0, 10);
const d = (iso) => (iso ? fmtDate(iso) : "—");

export function CarePlanReviews({ onChanged }) {
  const fields = [
    { key: "resident_ref", label: "Resident (room / initials)", placeholder: "Room 4 or J.S." },
    { key: "last_reviewed", label: "Last reviewed", type: "date" },
    { key: "interval_days", label: "Review every", type: "select", options: [{ value: 30, label: "Monthly" }, { value: 90, label: "Quarterly" }, { value: 180, label: "6-monthly" }, { value: 365, label: "Annually" }] },
    { key: "next_due", label: "Next due", readOnly: true },
    { key: "reviewed_by", label: "Reviewed by", placeholder: "Name / role" },
    { key: "notes", label: "Notes", placeholder: "Changes made, concerns…" },
  ];
  return (
    <RecordsLog id="care-plan-reviews" testId="care-plans" title="Oral care plan reviews" subtitle="Log each resident's last review — the next due date and overdue reviews feed the Overview automatically."
      endpoint="/compliance/care-plans" fields={fields} addLabel="Log review" sortKey="next_due" onChanged={onChanged}
      blank={() => ({ resident_ref: "", last_reviewed: today(), interval_days: 30, reviewed_by: "", notes: "" })}
      emptyText="No care plan reviews logged yet. Add one row per resident — use a room number or initials, not full names."
      columns={{ last_reviewed: (r) => d(r.last_reviewed), next_due: (r) => <span className="inline-flex items-center gap-2">{d(r.next_due)}<Pill state={r.state} testId={`care-plan-state-${r.id}`} /></span>,
        interval_days: (r) => ({ 30: "Monthly", 90: "Quarterly", 180: "6-monthly", 365: "Annually" }[r.interval_days] || `${r.interval_days} days`) }} />
  );
}

export function DentalReferrals({ onChanged }) {
  const fields = [
    { key: "resident_ref", label: "Resident (room / initials)", placeholder: "Room 7" },
    { key: "referred_on", label: "Referred on", type: "date" },
    { key: "reason", label: "Reason", placeholder: "e.g. broken denture, pain" },
    { key: "provider", label: "Dental provider", placeholder: "Practice / community dental" },
    { key: "status", label: "Status", type: "select", options: [{ value: "open", label: "Open" }, { value: "appointment_booked", label: "Appointment booked" }, { value: "completed", label: "Completed" }, { value: "cancelled", label: "Cancelled" }] },
    { key: "appointment_date", label: "Appointment", type: "date" },
    { key: "notes", label: "Outcome / notes", placeholder: "Treatment, follow-up…" },
  ];
  return (
    <RecordsLog id="dental-referrals" testId="referrals" title="Dental referrals" subtitle="Track every referral to completion — open referrals older than 28 days are flagged as overdue in the Overview."
      endpoint="/compliance/referrals" fields={fields} addLabel="Log referral" sortKey="referred_on" sortDesc onChanged={onChanged}
      blank={() => ({ resident_ref: "", referred_on: today(), reason: "", provider: "", status: "open", appointment_date: "", notes: "" })}
      emptyText="No dental referrals logged yet."
      columns={{ referred_on: (r) => d(r.referred_on), appointment_date: (r) => d(r.appointment_date),
        status: (r) => <span className="inline-flex flex-col gap-1"><Pill state={r.status} testId={`referral-status-${r.id}`} />{r.state === "overdue" && <Pill state="overdue" testId={`referral-overdue-${r.id}`} />}</span> }} />
  );
}
